import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  FileEdit, 
  Calendar, 
  LogOut, 
  Settings,
  User,
  Menu,
  X,
  Globe,
  FolderOpen,
  Shield,
  Link2,
  ChevronDown,
  Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supportedLanguages } from '../i18n';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
// PackageBadge removed from sidebar per product decision
import { TrialGuard } from '../components/subscription/TrialGuard';
import CreditsNotice from '../components/subscription/CreditsNotice';
import { ProjectSwitcher } from '../components/projects/ProjectSwitcher';
import InboxPanel from '../components/InboxPanel';
import SafeModeBanner from '../components/SafeModeBanner';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const navIcons = [Sparkles, FileEdit, Calendar, Settings];
const navPaths = ['/app/kreation', '/app/produktion', '/app/distribution', '/app/einstellungen'];

const DashboardLayout = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, activeProjectId, setActiveProjectId } = useAuth();
  const { t, lang, setLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeProjectName, setActiveProjectName] = useState('');
  const [mobileProjectMenuOpen, setMobileProjectMenuOpen] = useState(false);
  const [mobileProjects, setMobileProjects] = useState([]);
  const upgradeChecked = useRef(false);

  const isAdmin = user?.email === 'ashley@xchannels.ai';
  const isImpersonating = !!localStorage.getItem('admin_original_token');

  const handleProjectSwitch = () => {
    setRefreshKey(k => k + 1);
  };

  // Keep project name in sync
  useEffect(() => {
    if (!activeProjectId) return;
    const fetchName = async () => {
      try {
        const { data } = await axios.get(`${API}/projects/active`, { headers: getAuthHeader() });
        setActiveProjectName(data.project?.name || '');
      } catch {}
    };
    fetchName();
  }, [activeProjectId, getAuthHeader, refreshKey]);

  // Load projects for the mobile-header project popover
  useEffect(() => {
    let cancelled = false;
    if (!mobileProjectMenuOpen) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
        if (!cancelled) setMobileProjects(data.projects || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [mobileProjectMenuOpen, getAuthHeader]);

  // Close mobile header project popover whenever the hamburger opens
  useEffect(() => {
    if (mobileMenuOpen) setMobileProjectMenuOpen(false);
  }, [mobileMenuOpen]);

  const handleMobileHeaderSwitch = async (projectId) => {
    if (projectId === activeProjectId) { setMobileProjectMenuOpen(false); return; }
    setMobileProjectMenuOpen(false);
    await setActiveProjectId(projectId);
    toast.success(lang === 'en' ? 'Project switched' : 'Projekt gewechselt');
    setTimeout(() => setRefreshKey(k => k + 1), 150);
  };

  const navItems = [
    { path: navPaths[0], label: t.nav.kreation, icon: navIcons[0], description: t.nav.kreationDesc },
    { path: navPaths[1], label: t.nav.produktion, icon: navIcons[1], description: t.nav.produktionDesc },
    { path: navPaths[2], label: t.nav.distribution, icon: navIcons[2], description: t.nav.distributionDesc },
    { path: navPaths[3], label: t.nav.settings, icon: navIcons[3], description: t.nav.settingsDesc },
    ...((isAdmin && !isImpersonating) ? [
      { path: '/app/admin', label: 'Admin', icon: Shield, description: 'User verwalten' },
      { path: '/app/admin/postpeer', label: 'PostPeer', icon: Link2, description: 'Mapping verwalten' },
    ] : []),
  ];

  // Auto-upgrade Brand DNA for existing users
  useEffect(() => {
    if (!user || upgradeChecked.current) return;
    upgradeChecked.current = true;

    const checkAndUpgrade = async () => {
      try {
        const headers = getAuthHeader();
        if (!headers.Authorization) return;
        
        const { data } = await axios.get(`${API}/brand/upgrade-status`, { headers });
        if (!data.needs_upgrade) return;

        // Trigger background upgrade
        const res = await axios.post(`${API}/brand/auto-upgrade`, {}, { headers });
        if (res.data.success && res.data.reason === 'upgraded') {
          toast.success(t.nav.brandUpgraded, {
            duration: 6000,
          });
        }
      } catch (err) {
        // Silently fail - not critical
        console.error('Brand DNA auto-upgrade check failed:', err);
      }
    };

    checkAndUpgrade();
  }, [user, getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#0A1628]" data-testid="dashboard-outer">
      <SafeModeBanner />
      <div className="flex-1 flex flex-col lg:flex-row" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <header className="lg:hidden flex items-center justify-between p-4 border-b border-[#1A2B42]">
        <div className="flex items-center gap-3 min-w-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
            alt="xchannels.ai" 
            className="h-8 shrink-0"
          />
          {activeProjectName && (
            <Popover open={mobileProjectMenuOpen} onOpenChange={setMobileProjectMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-[#00D9C8] bg-[#00D9C8]/10 hover:bg-[#00D9C8]/20 active:bg-[#00D9C8]/30 px-2 py-1 rounded truncate max-w-[160px] transition-colors"
                  data-testid="mobile-project-badge"
                  aria-haspopup="menu"
                  aria-expanded={mobileProjectMenuOpen}
                >
                  <span className="truncate">{activeProjectName}</span>
                  <ChevronDown className="w-3 h-3 shrink-0 opacity-70" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                className="w-64 max-w-[calc(100vw-32px)] p-1 bg-[#1A2B42] border border-[#0A1628] rounded-lg z-50"
                data-testid="mobile-header-project-menu"
              >
                {mobileProjects.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-[#5A6C7D]">Lade Projekte…</div>
                ) : mobileProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => handleMobileHeaderSwitch(project.id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${project.id === activeProjectId ? 'bg-[#0A1628] text-white' : 'text-[#A0AEC0] hover:bg-[#0A1628]/60 hover:text-white'}`}
                    data-testid={`mobile-header-project-item-${project.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="w-4 h-4 text-[#00D9C8] shrink-0" />
                      <span className="truncate">{project.name}</span>
                    </span>
                    {project.id === activeProjectId && (
                      <Check className="w-4 h-4 text-[#00D9C8] shrink-0" />
                    )}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-white"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'tween', duration: 0.2 }}
            className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-[#0A1628] border-r border-[#1A2B42] z-50 flex flex-col"
          >
            {/* Logo */}
            <div className="p-4 border-b border-[#1A2B42] flex items-center justify-between">
              <img 
                src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
                alt="xchannels.ai" 
                className="h-8"
              />
              <button onClick={closeMobileMenu} className="p-2 text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 overflow-y-auto">
              {/* Project Switcher - Mobile */}
              <div className="mb-4">
                <ProjectSwitcher onSwitch={handleProjectSwitch} />
              </div>

              <ul className="space-y-1">
                {navItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={({ isActive }) => `
                          flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                          ${isActive 
                            ? 'bg-[#1A2B42] text-[#00D9C8]' 
                            : 'text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42]/50'
                          }
                        `}
                      >
                        <Icon className="w-5 h-5" />
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-xs opacity-70">{item.description}</p>
                        </div>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
              
              {/* Usage Status */}
              <div className="mt-6">
                
              </div>
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-[#1A2B42]">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-[#00D9C8]/20 text-[#00D9C8]">
                    {user?.name ? getInitials(user.name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-white">{user?.name || t.nav.user}</p>
                  <p className="text-xs text-[#5A6C7D] truncate max-w-[180px]">{user?.email}</p>
                </div>
              </div>
              {/* Language Toggle - Mobile (reuses desktop logic) */}
              <div className="mb-3 flex items-center gap-2 px-1">
                <Globe className="w-4 h-4 text-[#5A6C7D]" />
                <div className="flex gap-1">
                  {supportedLanguages.map(l => (
                    <button
                      key={l.key}
                      onClick={() => setLanguage(l.key)}
                      data-testid={`mobile-lang-toggle-${l.key}`}
                      className={`px-2 py-1 text-xs rounded transition-all ${
                        lang === l.key
                          ? 'bg-[#00D9C8]/20 text-[#00D9C8] font-semibold'
                          : 'text-[#5A6C7D] hover:text-white'
                      }`}
                    >
                      {l.flag}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-red-400 hover:bg-[#1A2B42] hover:text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t.nav.logout}
              </Button>
              
              {/* Legal Links - Mobile */}
              <div className="mt-4 pt-4 border-t border-[#1A2B42] flex gap-3 text-xs text-[#5A6C7D]">
                <Link to="/impressum" onClick={closeMobileMenu} className="hover:text-[#A0AEC0]">
                  {t.nav.impressum}
                </Link>
                <Link to="/datenschutz" onClick={closeMobileMenu} className="hover:text-[#A0AEC0]">
                  {t.nav.datenschutz}
                </Link>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-[#1A2B42] flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-[#1A2B42]">
          <img 
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
            alt="xchannels.ai" 
            className="h-10"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          {/* Project Switcher - Desktop */}
          <div className="mb-4">
            <ProjectSwitcher onSwitch={handleProjectSwitch} />
          </div>

          <ul className="space-y-1">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-3 transition-all
                      ${isActive 
                        ? 'bg-[#1A2B42] text-[#00D9C8] border-l-2 border-[#00D9C8]' 
                        : 'text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42]/50'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs opacity-70">{item.description}</p>
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
          
          {/* Usage Status */}
          <div className="mt-6 px-2">
            
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#1A2B42]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 px-3 h-auto py-3 hover:bg-[#1A2B42]"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#00D9C8]/20 text-[#00D9C8] text-sm">
                    {user?.name ? getInitials(user.name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{user?.name || t.nav.user}</p>
                  <p className="text-xs text-[#5A6C7D] truncate max-w-[140px]">
                    {user?.email}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-[#1A2B42] border-[#0A1628]">
              <DropdownMenuItem className="text-white hover:bg-[#0A1628]" data-testid="profile-menu-item">
                <User className="w-4 h-4 mr-2" />
                {t.nav.profile}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-white hover:bg-[#0A1628]" data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" />
                {t.nav.settings}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#0A1628]" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 focus:text-red-400 hover:bg-[#0A1628]"
                data-testid="logout-menu-item"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t.nav.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Language Toggle */}
          <div className="mt-3 flex items-center gap-2 px-3">
            <Globe className="w-4 h-4 text-[#5A6C7D]" />
            <div className="flex gap-1">
              {supportedLanguages.map(l => (
                <button
                  key={l.key}
                  onClick={() => setLanguage(l.key)}
                  data-testid={`lang-toggle-${l.key}`}
                  className={`px-2 py-1 text-xs rounded transition-all ${
                    lang === l.key
                      ? 'bg-[#00D9C8]/20 text-[#00D9C8] font-semibold'
                      : 'text-[#5A6C7D] hover:text-white'
                  }`}
                >
                  {l.flag}
                </button>
              ))}
            </div>
          </div>

          {/* Legal Links - Desktop */}
          <div className="mt-4 pt-4 border-t border-[#1A2B42] flex gap-3 text-xs text-[#5A6C7D]">
            <Link to="/impressum" className="hover:text-[#A0AEC0]" data-testid="sidebar-impressum">
              {t.nav.impressum}
            </Link>
            <Link to="/datenschutz" className="hover:text-[#A0AEC0]" data-testid="sidebar-datenschutz">
              {t.nav.datenschutz}
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        {/* Inbox bell — floats top-right of main content */}
        <div className="absolute top-4 right-4 lg:top-6 lg:right-6 z-20">
          <InboxPanel />
        </div>
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 lg:px-8 py-2 flex items-center justify-between" data-testid="impersonation-top-banner">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 text-xs font-medium">
                Impersonation: <span className="text-white">{user?.email}</span>
              </span>
            </div>
            <button
              onClick={() => {
                const adminTok = localStorage.getItem('admin_original_token');
                if (adminTok) {
                  localStorage.setItem('token', adminTok);
                  localStorage.removeItem('admin_original_token');
                  localStorage.removeItem('user');
                  window.location.href = '/app/admin';
                }
              }}
              className="text-xs text-amber-400 hover:text-amber-300 font-medium"
            >
              Zurück zum Admin
            </button>
          </div>
        )}
        {/* Desktop Project Bar */}
        {activeProjectName && (
          <div className="hidden lg:flex items-center gap-2 px-8 pt-6 pb-0" data-testid="desktop-project-bar">
            <FolderOpen className="w-4 h-4 text-[#00D9C8]" />
            <span className="text-sm font-medium text-[#00D9C8]" data-testid="desktop-project-name">
              {activeProjectName}
            </span>
          </div>
        )}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="p-4 lg:p-8"
        >
          <TrialGuard>
            <CreditsNotice />
            <Outlet key={refreshKey} />
          </TrialGuard>
        </motion.div>
      </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
